import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Wallet, Euro, Phone, TrendingDown, TrendingUp, CreditCard,
  ArrowUpRight, ArrowDownRight, Clock, BarChart3
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger
} from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOP_UP_OPTIONS = [10, 25, 50, 100, 200];

export default function KostenPage() {
  const [wallet, setWallet] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [topUpLoading, setTopUpLoading] = useState(false);
  const [showTopUp, setShowTopUp] = useState(false);
  const [customAmount, setCustomAmount] = useState("");

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [walletRes, txRes] = await Promise.all([
        axios.get(`${API}/wallet`),
        axios.get(`${API}/wallet/transactions`)
      ]);
      setWallet(walletRes.data);
      setTransactions(Array.isArray(txRes.data) ? txRes.data : []);
    } catch (e) {
      console.error("Failed to fetch wallet:", e);
    } finally {
      setLoading(false);
    }
  };

  const handleTopUp = async (amount) => {
    setTopUpLoading(true);
    try {
      const formData = new FormData();
      formData.append("amount", amount);
      const res = await axios.post(`${API}/wallet/top-up`, formData);
      toast.success(res.data.message);
      setShowTopUp(false);
      setCustomAmount("");
      fetchData();
    } catch (e) {
      toast.error("Fehler beim Aufladen");
    } finally {
      setTopUpLoading(false);
    }
  };

  const formatDate = (d) => {
    if (!d) return "–";
    return new Intl.DateTimeFormat("de-DE", {
      day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit"
    }).format(new Date(d));
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-14 bg-slate-200 rounded-lg" />
        <div className="grid grid-cols-4 gap-4">{[1,2,3,4].map(i => <div key={i} className="h-28 bg-slate-200 rounded-lg" />)}</div>
      </div>
    );
  }

  const chargeCount = transactions.filter(t => t.type === "call_charge").length;
  const topUpCount = transactions.filter(t => t.type === "top_up").length;
  const avgCostPerCall = chargeCount > 0 ? (wallet?.total_charged || 0) / chargeCount : 0;

  return (
    <div className="space-y-6" data-testid="kosten-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 tracking-tight" data-testid="kosten-title">
            Kosten & Guthaben
          </h1>
          <p className="text-slate-500 mt-1">Verwalten Sie Ihr Guthaben und sehen Sie Ihre Anrufkosten</p>
        </div>
        <Dialog open={showTopUp} onOpenChange={setShowTopUp}>
          <DialogTrigger asChild>
            <Button className="bg-[#405189] hover:bg-[#354270] text-white gap-2" data-testid="top-up-btn">
              <CreditCard className="w-4 h-4" />
              Guthaben aufladen
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Guthaben aufladen</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-2">
              <p className="text-sm text-slate-500">Wählen Sie einen Betrag oder geben Sie einen eigenen ein:</p>
              <div className="grid grid-cols-3 gap-2">
                {TOP_UP_OPTIONS.map(amount => (
                  <Button
                    key={amount}
                    variant="outline"
                    onClick={() => handleTopUp(amount)}
                    disabled={topUpLoading}
                    className="h-12 text-base font-semibold hover:bg-[#405189] hover:text-white transition-colors"
                    data-testid={`topup-${amount}`}
                  >
                    {amount} €
                  </Button>
                ))}
                <input
                  type="number"
                  placeholder="Eigener"
                  value={customAmount}
                  onChange={(e) => setCustomAmount(e.target.value)}
                  className="h-12 text-center text-base font-semibold border border-slate-200 rounded-md px-2 focus:outline-none focus:ring-2 focus:ring-[#405189] w-full"
                  data-testid="custom-amount-input"
                />
              </div>
              {customAmount && parseFloat(customAmount) > 0 && (
                <Button
                  onClick={() => handleTopUp(parseFloat(customAmount))}
                  disabled={topUpLoading}
                  className="w-full bg-[#0ab39c] hover:bg-[#099b87] text-white h-10"
                  data-testid="custom-topup-btn"
                >
                  {topUpLoading ? "Wird verarbeitet..." : `${parseFloat(customAmount).toFixed(2)} € aufladen`}
                </Button>
              )}
              <div className="flex items-center gap-2 text-[11px] text-slate-400 border-t pt-3">
                <CreditCard className="w-3.5 h-3.5" />
                <span>Sichere Zahlung via Stripe</span>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-white shadow-sm border border-slate-200/60">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Aktuelles Guthaben</span>
            </div>
            <div className="flex items-end justify-between">
              <h3 className="text-[22px] font-bold text-slate-800">{(wallet?.balance || 0).toFixed(2)} €</h3>
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <Wallet className="w-5 h-5 text-green-600" />
              </div>
            </div>
            <p className="text-[10px] text-slate-400 mt-2">Verfügbar für Anrufe</p>
          </CardContent>
        </Card>

        <Card className="bg-white shadow-sm border border-slate-200/60">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Gesamtkosten</span>
              <span className="text-[11px] font-semibold text-[#f06548] flex items-center gap-0.5">
                <ArrowDownRight className="w-3 h-3" />{(wallet?.total_charged || 0).toFixed(2)} €
              </span>
            </div>
            <div className="flex items-end justify-between">
              <h3 className="text-[22px] font-bold text-slate-800">{chargeCount} Anrufe</h3>
              <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                <TrendingDown className="w-5 h-5 text-red-500" />
              </div>
            </div>
            <p className="text-[10px] text-slate-400 mt-2">Bisher abgerechnet</p>
          </CardContent>
        </Card>

        <Card className="bg-white shadow-sm border border-slate-200/60">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Aufgeladen</span>
              <span className="text-[11px] font-semibold text-[#0ab39c] flex items-center gap-0.5">
                <ArrowUpRight className="w-3 h-3" />{topUpCount}x
              </span>
            </div>
            <div className="flex items-end justify-between">
              <h3 className="text-[22px] font-bold text-slate-800">{(wallet?.total_topped_up || 0).toFixed(2)} €</h3>
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-blue-600" />
              </div>
            </div>
            <p className="text-[10px] text-slate-400 mt-2">Gesamt aufgeladen</p>
          </CardContent>
        </Card>

        <Card className="bg-white shadow-sm border border-slate-200/60">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Ø Kosten/Anruf</span>
            </div>
            <div className="flex items-end justify-between">
              <h3 className="text-[22px] font-bold text-slate-800">{avgCostPerCall.toFixed(2)} €</h3>
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <BarChart3 className="w-5 h-5 text-amber-600" />
              </div>
            </div>
            <p className="text-[10px] text-slate-400 mt-2">{wallet?.cost_per_minute?.toFixed(2) || "0.10"} € pro angef. Minute</p>
          </CardContent>
        </Card>
      </div>

      {/* Pricing Info */}
      <Card className="bg-[#f8f9fa] border border-slate-200/60 shadow-sm">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-[#405189] flex items-center justify-center shrink-0 mt-0.5">
              <Euro className="w-4 h-4 text-white" />
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-800">Preismodell</p>
              <p className="text-xs text-slate-500 mt-1">
                Pro angefangene Minute wird <strong>{wallet?.cost_per_minute?.toFixed(2) || "0.10"} €</strong> berechnet.
                Ein 10-Sekunden-Anruf kostet 0,10 €, ein 61-Sekunden-Anruf kostet 0,20 €.
                Ihr Guthaben wird automatisch nach jedem Anruf abgezogen.
                Ohne ausreichendes Guthaben können keine Anrufe gestartet werden.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Transactions Table */}
      <Card className="bg-white border border-slate-200/60 shadow-sm overflow-hidden">
        <CardHeader className="pb-0 pt-4 px-4">
          <CardTitle className="text-sm font-semibold text-slate-800">Transaktionshistorie</CardTitle>
        </CardHeader>
        <CardContent className="p-0 mt-3">
          <Table data-testid="transactions-table">
            <TableHeader>
              <TableRow className="bg-slate-50/50 border-y border-slate-200">
                <TableHead className="font-semibold text-slate-700 w-10"></TableHead>
                <TableHead className="font-semibold text-slate-700">Typ</TableHead>
                <TableHead className="font-semibold text-slate-700">Beschreibung</TableHead>
                <TableHead className="font-semibold text-slate-700">Betrag</TableHead>
                <TableHead className="font-semibold text-slate-700">Guthaben danach</TableHead>
                <TableHead className="font-semibold text-slate-700">Datum</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {transactions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-12 text-slate-400">
                    Noch keine Transaktionen
                  </TableCell>
                </TableRow>
              ) : transactions.map(tx => (
                <TableRow key={tx.id} className="border-b border-slate-100 hover:bg-slate-50/40" data-testid={`tx-${tx.id}`}>
                  <TableCell>
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center ${
                      tx.type === "top_up" ? "bg-green-100" : "bg-red-100"
                    }`}>
                      {tx.type === "top_up"
                        ? <ArrowUpRight className="w-3.5 h-3.5 text-green-600" />
                        : <Phone className="w-3.5 h-3.5 text-red-500" />
                      }
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                      tx.type === "top_up" ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"
                    }`}>
                      {tx.type === "top_up" ? "Aufladung" : "Anrufkosten"}
                    </span>
                  </TableCell>
                  <TableCell className="text-sm text-slate-600">{tx.description}</TableCell>
                  <TableCell className={`font-semibold text-sm ${tx.amount >= 0 ? "text-green-600" : "text-red-600"}`}>
                    {tx.amount >= 0 ? "+" : ""}{parseFloat(tx.amount).toFixed(2)} €
                  </TableCell>
                  <TableCell className="text-sm text-slate-600">{parseFloat(tx.balance_after).toFixed(2)} €</TableCell>
                  <TableCell className="text-sm text-slate-500">{formatDate(tx.created_at)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
